// VirtualizedFolderTree.jsx
// Virtualized, flat-rendered folder tree with on-demand loading and expand hints.

// Copyright Viktor Eberhardt, 2025
// Author: Viktor Eberhardt

// Framework imports
import React from "react";
import { useHistory } from "react-router-dom";
import PropTypes from "prop-types";

// External imports
import { FixedSizeList } from "react-window";

// MUI imports (single-line)
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FolderOpenOutlinedIcon from "@mui/icons-material/FolderOpenOutlined";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import CircularProgress from "@mui/material/CircularProgress";

// HSA imports
import Backend from "../../common/utils/Backend";
import FileFormatUtils from "../../common/utils/FileFormatUtils";
import { FileViewerContext } from "../FileViewer";
import { SortByType } from "../FileViewer";

const ROW_HEIGHT = 28; // px per row
const INDENT_PX = 16; // px per nesting level

function useResizeObserver(ref) {
  const [size, setSize] = React.useState({ width: 0, height: 0 });
  React.useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    let frame = null;
    const ro = new ResizeObserver((entries) => {
      const e = entries[0];
      if (!e) return;
      const { width, height } = e.contentRect;
      // batch via rAF
      if (frame) cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => setSize({ width, height }));
    });
    ro.observe(el);
    return () => {
      if (frame) cancelAnimationFrame(frame);
      ro.disconnect();
    };
  }, [ref]);
  return size;
}

// Node shape for flat list rendering
// { path, type: 'directory'|'file', depth, childCount: number|null, loaded: bool, loading: bool, children?: Node[] }

export default function VirtualizedFolderTree() {
  const FV = React.useContext(FileViewerContext);
  const history = useHistory();
  const containerRef = React.useRef(null);
  const { height, width } = useResizeObserver(containerRef);
  const listRef = React.useRef(null);
  const outerRef = React.useRef(null); // scrollable DOM element of react-window
  const isMountedRef = React.useRef(true);

  React.useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Map path -> node info; each node holds children when loaded
  const [nodeMap, setNodeMap] = React.useState(new Map());
  // Flat array of visible nodes
  const [flatNodes, setFlatNodes] = React.useState([]);
  const [loadingRoot, setLoadingRoot] = React.useState(false);

  // nodeMap is the single source of truth for nodes; avoid duplicating state

  // Helper to load children for a directory path
  const loadChildren = React.useCallback((dirPath) => {
    // Mark as loading
    setNodeMap((prev) => {
      const next = new Map(prev);
      const me = next.get(dirPath);
      if (me && me.type === "directory" && !me.loaded && !me.loading) {
        next.set(dirPath, { ...me, loading: true });
      }
      return next;
    });

    Backend.walkDir(dirPath, true, true)
      .then((kids) => {
        if (!isMountedRef.current) return;
        const filtered = FileFormatUtils.filterViewerEntities(kids);
        setNodeMap((prev) => {
          const next = new Map(prev);
          const me = next.get(dirPath) || {
            path: dirPath,
            type: "directory",
            depth: 0,
            parentPath: "",
          };
          const children = filtered.map((e) => ({
            path: e.path,
            type: e.type,
            depth: (me.depth || 0) + 1,
            childCount: Number.isFinite(e.childCount) ? e.childCount : null,
            loaded: false,
            loading: false,
            parentPath: me.path,
            children: undefined,
            // carry over optional metadata used for sorting
            creationTime: e.creationTime || undefined,
          }));
          next.set(dirPath, { ...me, loaded: true, loading: false, children });
          for (const c of children) next.set(c.path, c);
          return next;
        });
      })
      .catch(() => {
        // Clear loading flag on error
        if (!isMountedRef.current) return;
        setNodeMap((prev) => {
          const next = new Map(prev);
          const me = next.get(dirPath);
          if (me) next.set(dirPath, { ...me, loading: false });
          return next;
        });
      });
  }, []);

  // Normalized expanded set for robust matching regardless of slash direction
  const normalizePath = React.useCallback(
    (p) => (typeof p === "string" ? p.replace(/\\/g, "/") : p),
    [],
  );
  const expandedSet = React.useMemo(() => {
    const arr = Array.isArray(FV.expanded) ? FV.expanded : [];
    return new Set(arr.map((p) => normalizePath(p)));
  }, [FV.expanded, normalizePath]);
  const isExpanded = React.useCallback(
    (path) => expandedSet.has(normalizePath(path)),
    [expandedSet, normalizePath],
  );

  // Helper: sort nodes with separate criteria for directories vs files to match preview ordering
  const sortNodesForDisplay = React.useCallback(
    (nodes) => {
      const list = Array.isArray(nodes) ? nodes : [];
      const dirs = list.filter((n) => n.type === "directory");
      const files = list.filter((n) => n.type !== "directory");

      // Directories: use folder sort settings
      const sortedDirs = FileFormatUtils.sortEntitiesByTypeAndName(
        dirs,
        FV.folderSortBy,
        FV.folderSortReverse,
        false,
      ).filter((n) => n.type === "directory");

      // Files: use preview sort settings
      let sortedFiles = [];
      if (FV.filePreviewSortBy === SortByType.AdditionalName) {
        const getFileName = (p) => (p ? p.split(/[\\/]+/).pop() : "");
        const extractLeadingNumber = (s) => {
          const m = /^\s*(\d+)/.exec(s || "");
          return m ? parseInt(m[1], 10) : null;
        };
        const withKeys = files.map((f) => {
          const key = (f.additionalName || getFileName(f.path) || "")
            .toString()
            .toLowerCase();
          return { f, key };
        });
        withKeys.sort((a, b) => {
          const aNum = extractLeadingNumber(a.key);
          const bNum = extractLeadingNumber(b.key);
          if (aNum !== null && bNum !== null && aNum !== bNum) {
            return aNum - bNum;
          }
          if (a.key !== b.key) return a.key.localeCompare(b.key);
          const an = getFileName(a.f.path).toLowerCase();
          const bn = getFileName(b.f.path).toLowerCase();
          if (an !== bn) return an.localeCompare(bn);
          return a.f.path.localeCompare(b.f.path);
        });
        sortedFiles = FV.filePreviewSortReverse
          ? withKeys.reverse().map((x) => x.f)
          : withKeys.map((x) => x.f);
      } else if (FV.filePreviewSortBy === SortByType.Name) {
        const getFileName = (p) => (p ? p.split(/[\\/]+/).pop() : "");
        const extractLeadingNumber = (s) => {
          const m = /^\s*(\d+)/.exec((s || "").toString());
          return m ? parseInt(m[1], 10) : null;
        };
        const withKeys = files.map((f) => ({
          f,
          key: getFileName(f.path).toLowerCase(),
        }));
        withKeys.sort((a, b) => {
          const aNum = extractLeadingNumber(a.key);
          const bNum = extractLeadingNumber(b.key);
          if (aNum !== null && bNum !== null && aNum !== bNum) {
            return aNum - bNum;
          }
          if (a.key !== b.key) return a.key.localeCompare(b.key);
          return a.f.path.localeCompare(b.f.path);
        });
        sortedFiles = FV.filePreviewSortReverse
          ? withKeys.reverse().map((x) => x.f)
          : withKeys.map((x) => x.f);
      } else if (FV.filePreviewSortBy === SortByType.Type) {
        const withKeys = files.map((f) => ({
          f,
          key: (f.path.split(".").pop() || "").toLowerCase(),
        }));
        withKeys.sort((a, b) => {
          if (a.key !== b.key) return a.key.localeCompare(b.key);
          const an = a.f.path.toLowerCase();
          const bn = b.f.path.toLowerCase();
          return an.localeCompare(bn);
        });
        sortedFiles = FV.filePreviewSortReverse
          ? withKeys.reverse().map((x) => x.f)
          : withKeys.map((x) => x.f);
      } else if (
        FV.filePreviewSortBy === SortByType.Date ||
        FV.filePreviewSortBy === SortByType.ChangeDate
      ) {
        // Backend returns creationTime as sortable string 'yyyy/MM/dd HH:mm:ss'
        const withKeys = files.map((f) => ({ f, key: f.creationTime || "" }));
        withKeys.sort((a, b) => {
          if (a.key !== b.key) return a.key.localeCompare(b.key);
          // tie-breaker by name
          const an = a.f.path.toLowerCase();
          const bn = b.f.path.toLowerCase();
          return an.localeCompare(bn);
        });
        sortedFiles = FV.filePreviewSortReverse
          ? withKeys.reverse().map((x) => x.f)
          : withKeys.map((x) => x.f);
      } else {
        // Default: keep original order
        sortedFiles = files.slice();
      }

      return [...sortedDirs, ...sortedFiles];
    },
    [
      FV.folderSortBy,
      FV.folderSortReverse,
      FV.filePreviewSortBy,
      FV.filePreviewSortReverse,
    ],
  );

  const recomputeFlat = React.useCallback(() => {
    const rootChildren = [];
    // Find root-level nodes (those without a parent in their path under base dir inference)
    // We rely on the top-level load populating nodeMap entries with parentPath = "" for roots.
    for (const [, node] of nodeMap) {
      if (node.parentPath === "") rootChildren.push(node);
    }
    // Sort roots using the same logic as children: directories with folder sort, files with preview sort
    const sortedRoots = sortNodesForDisplay(rootChildren);

    const out = [];
    const pushSubtree = (n) => {
      out.push(n);
      if (n.type !== "directory") return;
      if (!isExpanded(n.path)) return;
      // If this directory is expanded but not yet loaded, show a loading placeholder row
      if (!n.loaded && (n.childCount === null || n.childCount > 0)) {
        if (n.loading) {
          out.push({
            path: `${n.path}::loading`,
            type: "loading",
            depth: (n.depth || 0) + 1,
            parentPath: n.path,
          });
        }
        return; // don't descend further until loaded
      }

      const kids = Array.isArray(n.children) ? n.children : [];
      // Resolve each child to the latest entry from nodeMap so deeper expansions propagate
      const resolvedKids = kids.map((k) => nodeMap.get(k.path) || k);
      const sortedKids = sortNodesForDisplay(resolvedKids);
      for (const k of sortedKids) pushSubtree(k);
    };

    for (const r of sortedRoots) pushSubtree(r);
    setFlatNodes(out);
  }, [
    nodeMap,
    FV.folderSortBy,
    FV.folderSortReverse,
    FV.filePreviewSortBy,
    FV.filePreviewSortReverse,
    isExpanded,
    sortNodesForDisplay,
  ]);

  // Load top-level
  React.useEffect(() => {
    setLoadingRoot(true);
    Backend.walkDir("", true, true).then((entities) => {
      if (!isMountedRef.current) return;
      const filtered = FileFormatUtils.filterViewerEntities(entities);
      // Build nodes
      const map = new Map();
      filtered.forEach((e) => {
        map.set(e.path, {
          path: e.path,
          type: e.type,
          depth: 0,
          childCount: Number.isFinite(e.childCount) ? e.childCount : null,
          loaded: false,
          loading: false,
          parentPath: "",
          children: undefined,
          // carry over optional metadata used for sorting
          creationTime: e.creationTime || undefined,
        });
      });
      setNodeMap(map);
      setLoadingRoot(false);
    });
  }, []);

  // Recompute flat nodes whenever inputs change
  React.useEffect(() => {
    recomputeFlat();
  }, [
    nodeMap,
    FV.expanded,
    FV.folderSortBy,
    FV.folderSortReverse,
    FV.filePreviewSortBy,
    FV.filePreviewSortReverse,
    recomputeFlat,
  ]);

  // Auto-load children for any directories that are already expanded but not yet loaded.
  // Robust to deep expanded paths by walking up ancestor chain until a known node,
  // then triggering loads; subsequent nodeMap updates will cascade loads deeper.
  React.useEffect(() => {
    if (!Array.isArray(FV.expanded) || FV.expanded.length === 0) return;
    const getDirname = (p) => {
      if (!p) return "";
      const i1 = p.lastIndexOf("/");
      const i2 = p.lastIndexOf("\\");
      const i = Math.max(i1, i2);
      return i > 0 ? p.substring(0, i) : "";
    };
    const getNodeByPath = (p) => {
      if (!p) return undefined;
      // Try exact
      let n = nodeMap.get(p);
      if (n) return n;
      // Try swapped separators
      const alt = p.includes("/")
        ? p.replace(/\//g, "\\")
        : p.replace(/\\/g, "/");
      n = nodeMap.get(alt);
      return n;
    };
    const toLoad = new Set();
    for (const p of FV.expanded) {
      // Find nearest existing ancestor node in the current map
      let cur = p;
      while (cur && !getNodeByPath(cur)) {
        const next = getDirname(cur);
        if (next === cur) break;
        cur = next;
      }
      const n = getNodeByPath(cur || p);
      if (
        n &&
        n.type === "directory" &&
        !n.loaded &&
        !n.loading &&
        (n.childCount === null || n.childCount > 0)
      ) {
        toLoad.add(n.path);
      }
    }
    toLoad.forEach((path) => loadChildren(path));
  }, [FV.expanded, nodeMap, loadChildren]);

  // Persist and restore tree scroll position
  const TREE_SCROLL_KEY = "hsaFolderTreeScrollOffset";
  const didRestoreTreeScrollRef = React.useRef(false);
  // Determine when it's safe to restore scroll: all expanded directories are present and loaded (or known empty)
  const expandedReady = React.useMemo(() => {
    if (loadingRoot) return false;
    const expandedArr = Array.isArray(FV.expanded) ? FV.expanded : [];
    for (const p of expandedArr) {
      const n = nodeMap.get(p);
      if (!n) continue; // skip nodes not yet in map; they will be loaded on demand
      if (n.type === "directory") {
        if (n.loading) return false;
        if (!n.loaded && (n.childCount === null || n.childCount > 0))
          return false;
      }
    }
    return true;
  }, [FV.expanded, nodeMap, loadingRoot]);
  // Helper: attempt to restore tree scroll from localStorage once
  const tryRestoreTreeScroll = React.useCallback(() => {
    if (didRestoreTreeScrollRef.current) return;
    const offsetRaw = localStorage.getItem(TREE_SCROLL_KEY);
    const offset = offsetRaw ? parseInt(offsetRaw, 10) : 0;
    if (!Number.isFinite(offset) || offset <= 0) {
      didRestoreTreeScrollRef.current = true; // nothing to restore
      return;
    }
    // Defer to next frame to ensure list DOM exists
    requestAnimationFrame(() => {
      try {
        if (outerRef.current) {
          outerRef.current.scrollTop = offset;
          didRestoreTreeScrollRef.current = true;
        } else if (listRef.current) {
          listRef.current.scrollTo(offset);
          didRestoreTreeScrollRef.current = true;
        }
      } catch {
        // ignore
      }
    });
  }, []);

  // Restore once after tree and all expanded children finished loading
  React.useEffect(() => {
    if (!expandedReady) return;
    tryRestoreTreeScroll();
  }, [expandedReady, tryRestoreTreeScroll]);

  // Fallback: if readiness never flips (e.g., deep paths), attempt a delayed restore once after mount
  React.useEffect(() => {
    if (didRestoreTreeScrollRef.current) return;
    const t = setTimeout(() => {
      if (!didRestoreTreeScrollRef.current) tryRestoreTreeScroll();
    }, 1200);
    return () => clearTimeout(t);
  }, [tryRestoreTreeScroll]);

  // Save current tree scroll position to localStorage (called on meaningful clicks)
  const saveTreeScroll = React.useCallback(() => {
    try {
      const el = outerRef.current;
      if (el && typeof el.scrollTop === "number") {
        localStorage.setItem(
          TREE_SCROLL_KEY,
          String(Math.max(0, Math.floor(el.scrollTop))),
        );
      }
    } catch {
      // ignore persistence errors
    }
  }, []);

  // Helper: set or clear the fileId in the URL's query string (preserving other params)
  const updateUrlFileId = React.useCallback(
    (newFileId) => {
      try {
        const url = new URL(window.location.href);
        const current = url.searchParams.get("fileId");
        const next = newFileId == null ? null : String(newFileId);
        // Avoid pushing duplicate history entries if unchanged
        if ((current || null) === (next || null)) return;
        if (next == null || next === "") {
          url.searchParams.delete("fileId");
        } else {
          url.searchParams.set("fileId", next);
        }
        // Keep pathname intact, only change search
        const newHref = `${url.pathname}${url.search}${url.hash}`;
        history.push(newHref);
      } catch {
        // ignore URL errors
      }
    },
    [history],
  );

  // Also save tree scroll on page unload/visibility change so it persists even without a last click
  React.useEffect(() => {
    const onBeforeUnload = () => saveTreeScroll();
    const onVisibility = () => {
      if (document.visibilityState === "hidden") saveTreeScroll();
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.removeEventListener("beforeunload", onBeforeUnload);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [saveTreeScroll]);

  // Toggle expand/collapse and load children on demand
  const onToggleDir = React.useCallback(
    (node) => {
      // Persist scroll on user interaction
      saveTreeScroll();
      // Helper to test if a path lies within a directory subtree
      const isPathInside = (dir, p) => {
        if (!dir || !p) return false;
        const dn = normalizePath(dir);
        const pn = normalizePath(p);
        if (dn === pn) return true;
        return pn.startsWith(dn.endsWith("/") ? dn : dn + "/");
      };
      const already = isExpanded(node.path);
      const nodePathN = normalizePath(node.path);
      const curExpanded = Array.isArray(FV.expanded) ? FV.expanded : [];
      // Remove any entry equal to this path after normalization (handles slash differences)
      let nextExpandedArr = curExpanded.filter(
        (p) => normalizePath(p) !== nodePathN,
      );
      // If we are expanding, add the normalized path
      if (!already) nextExpandedArr = [...nextExpandedArr, nodePathN];

      // If expanding and children not loaded and there are children, fetch
      const needLoad =
        !already &&
        node.type === "directory" &&
        !node.loaded &&
        (node.childCount === null || node.childCount > 0);
      if (needLoad) loadChildren(node.path);

      FV.updateExpanded(nextExpandedArr);
      // Expanding: populate preview list, keep any current file selection intact
      if (!already) {
        Backend.walkDir(node.path, true).then((files) => {
          if (!isMountedRef.current) return;
          if (!Array.isArray(files)) return;
          const filePaths = files.filter((f) => f.type === "file");
          FV.setPathList(filePaths);
        });
      } else {
        // Collapsing: clear selection only if it lies within the collapsed subtree
        const collapsingDir = node.path;
        // Clear active file if its path is within the collapsed directory
        if (
          FV.activeFilePath &&
          isPathInside(collapsingDir, FV.activeFilePath)
        ) {
          FV.setActiveFileId(null);
          FV.setActiveFilePath(null);
          try {
            // Also remove fileId from URL
            updateUrlFileId(null);
          } catch {
            /* ignore URL update errors */
          }
        }
        // If the currently selected folder is the one being collapsed (or within it), clear it
        if (
          FV.selectedFolder?.path &&
          isPathInside(collapsingDir, FV.selectedFolder.path)
        ) {
          FV.setSelectedFolder(null);
        }
        // Clear preview list when collapsing the currently previewed folder subtree
        FV.setPathList([]);
      }
    },
    [FV, isExpanded, saveTreeScroll],
  );

  const onClickFile = React.useCallback(
    (node) => {
      // Persist scroll on user interaction
      saveTreeScroll();
      const clickedPath = node.path;
      // Immediately reflect the clicked file as selected to avoid a one-render lag
      FV.setActiveFilePath(clickedPath);
      // Reset preview list; it will be repopulated after fetching the directory contents
      FV.setPathList([]);
      const deriveDir = (p) => {
        if (!p) return "";
        const idx1 = p.lastIndexOf("/");
        const idx2 = p.lastIndexOf("\\");
        const idx = Math.max(idx1, idx2);
        return idx > 0 ? p.substring(0, idx) : "";
      };
      const parentPath = node.parentPath || deriveDir(clickedPath);
      if (parentPath) {
        Backend.walkDir(parentPath, true).then((files) => {
          if (!isMountedRef.current) return;
          if (!Array.isArray(files)) return;
          const filePaths = files.filter((f) => f.type === "file");
          FV.setPathList(filePaths);
          // Ensure active file path stays on the clicked file (id resolution may arrive later)
          FV.setActiveFilePath(clickedPath);
          // Resolve and set active file id using file info by path (more direct)
          Backend.getFileInfoByPath(clickedPath)
            .then((info) => {
              if (!isMountedRef.current) return;
              if (info && (info.id || info.Id)) {
                const id = info.id || info.Id;
                FV.setActiveFileId(String(id));
                updateUrlFileId(id);
              } else {
                // Fallback to sceneIds approach if needed
                return Backend.getFileIdData(clickedPath).then((data) => {
                  if (!isMountedRef.current) return;
                  const arr = Array.isArray(data?.sceneIds)
                    ? data.sceneIds
                    : [];
                  if (arr.length > 0) {
                    const first = arr[0];
                    const sid = typeof first === "object" ? first.id : first;
                    if (sid != null) {
                      FV.setActiveFileId(String(sid));
                      updateUrlFileId(sid);
                    }
                  }
                });
              }
            })
            .catch(() => {
              // best-effort: ignore errors
            });
        });
      } else {
        FV.setPathList([{ path: clickedPath, type: "file" }]);
        FV.setActiveFilePath(clickedPath);
        Backend.getFileInfoByPath(clickedPath)
          .then((info) => {
            if (!isMountedRef.current) return;
            if (info && (info.id || info.Id)) {
              const id = info.id || info.Id;
              FV.setActiveFileId(String(id));
              updateUrlFileId(id);
            } else {
              return Backend.getFileIdData(clickedPath).then((data) => {
                if (!isMountedRef.current) return;
                const arr = Array.isArray(data?.sceneIds) ? data.sceneIds : [];
                if (arr.length > 0) {
                  const first = arr[0];
                  const sid = typeof first === "object" ? first.id : first;
                  if (sid != null) {
                    FV.setActiveFileId(String(sid));
                    updateUrlFileId(sid);
                  }
                }
              });
            }
          })
          .catch(() => {});
      }
    },
    [FV, saveTreeScroll, updateUrlFileId],
  );

  const Row = ({ index, style }) => {
    const n = flatNodes[index];
    if (!n) return null;
    if (n.type === "loading") {
      return (
        <div
          style={{
            ...style,
            lineHeight: `${ROW_HEIGHT}px`,
            display: "flex",
            alignItems: "center",
            color: "#666",
          }}
        >
          <div
            style={{
              paddingLeft: n.depth * INDENT_PX,
              display: "flex",
              alignItems: "center",
              width: "100%",
              gap: 8,
            }}
          >
            <span style={{ width: 24, height: 24 }} />
            <CircularProgress
              size={14}
              thickness={5}
              style={{ marginRight: 6 }}
            />
            <span>Loading…</span>
          </div>
        </div>
      );
    }
    const isDir = n.type === "directory";
    const expanded = isDir && isExpanded(n.path);
    const canExpand = isDir && (n.childCount === null || n.childCount > 0);
    // Single-selection rule: if a file is active, only it is selected; otherwise the selected folder (if any)
    const isSelected = FV.activeFilePath
      ? !isDir && FV.activeFilePath === n.path
      : isDir && FV.selectedFolder?.path === n.path;

    return (
      <div
        style={{
          ...style,
          lineHeight: `${ROW_HEIGHT}px`,
          display: "flex",
          alignItems: "center",
          cursor: "pointer",
          backgroundColor: isSelected ? "rgba(25,118,210,0.12)" : undefined,
        }}
      >
        <div
          style={{
            paddingLeft: n.depth * INDENT_PX,
            display: "flex",
            alignItems: "center",
            width: "100%",
            userSelect: "none",
          }}
          onClick={() => {
            // Allow clicking anywhere on the row (except chevron which stops propagation) to act
            if (isDir) {
              // Persist scroll
              saveTreeScroll();
              FV.setSelectedFolder({ path: n.path, user: true });
              // Only clear selection when collapsing an expanded directory
              const currentlyExpanded = isExpanded(n.path);
              if (currentlyExpanded) {
                // If a file inside this folder is active, clear it and URL
                const isPathInside = (dir, p) => {
                  if (!dir || !p) return false;
                  const dn = normalizePath(dir);
                  const pn = normalizePath(p);
                  if (dn === pn) return true;
                  return pn.startsWith(dn.endsWith("/") ? dn : dn + "/");
                };
                if (
                  FV.activeFilePath &&
                  isPathInside(n.path, FV.activeFilePath)
                ) {
                  FV.setActiveFileId(null);
                  FV.setActiveFilePath(null);
                  try {
                    updateUrlFileId(null);
                  } catch {
                    /* ignore */
                  }
                }
                // If the selected folder is within the collapsed path, clear it
                if (
                  FV.selectedFolder?.path &&
                  isPathInside(n.path, FV.selectedFolder.path)
                ) {
                  FV.setSelectedFolder(null);
                }
                FV.setPathList([]);
              }
              const canExpandLocal = n.childCount === null || n.childCount > 0;
              if (canExpandLocal) onToggleDir(n);
            } else {
              onClickFile(n);
            }
          }}
        >
          <span
            onClick={
              canExpand
                ? (e) => {
                    // Only intercept click when the chevron can actually toggle
                    e.stopPropagation();
                    if (isDir) {
                      // Select folder on chevron toggle for consistent single selection
                      FV.setSelectedFolder({ path: n.path, user: true });
                      onToggleDir(n);
                    }
                  }
                : undefined
            }
            style={{
              width: 24,
              height: 24,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              opacity: canExpand ? 1 : 0,
              pointerEvents: canExpand ? "auto" : "none",
            }}
          >
            {canExpand ? (
              expanded ? (
                <ExpandMoreIcon fontSize="small" />
              ) : (
                <ChevronRightIcon fontSize="small" />
              )
            ) : null}
          </span>
          {isDir ? (
            <FolderOpenOutlinedIcon style={{ marginRight: 6 }} />
          ) : (
            <InsertDriveFileOutlinedIcon style={{ marginRight: 6 }} />
          )}
          <span
            style={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              flex: 1,
              fontWeight: isSelected ? 600 : 400,
            }}
          >
            {n.path.split(/[/\\]+/).pop()}
          </span>
        </div>
      </div>
    );
  };
  Row.propTypes = { index: PropTypes.number, style: PropTypes.object };
  // Busy when the root or any expanded directory is still loading
  const isBusy = !expandedReady;

  return (
    <div
      ref={containerRef}
      style={{ height: "100%", width: "100%", position: "relative" }}
      aria-busy={isBusy ? "true" : "false"}
      aria-live="polite"
    >
      {height > 0 ? (
        <FixedSizeList
          ref={listRef}
          outerRef={outerRef}
          height={height}
          width={width || "100%"}
          itemCount={flatNodes.length}
          itemSize={ROW_HEIGHT}
          overscanCount={12}
          itemKey={(idx) => flatNodes[idx]?.path || idx}
        >
          {Row}
        </FixedSizeList>
      ) : null}
      {loadingRoot && flatNodes.length === 0 ? (
        <div style={{ padding: 8, color: "#666" }}>Loading…</div>
      ) : null}
      {isBusy ? (
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(255,255,255,0.6)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 2,
            pointerEvents: "auto",
            cursor: "wait",
          }}
          role="status"
          aria-label="Folder tree loading"
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <CircularProgress size={18} thickness={5} />
            <span style={{ color: "#444" }}>Preparing tree…</span>
          </div>
        </div>
      ) : null}
    </div>
  );
}
